import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentVariablesService } from '@kenv';
import { DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { Promo } from '@kp/shared/components/promo/promo.model';
import { CardEventType, CardRequestType } from '@ktypes/enums';
import {
  CardEvent,
  CardItem,
  DeepLinkBase,
  DialogueDeepLinkData,
  Link,
  LinkType,
  QuestKeyType,
  getTypedDeepLinkData,
} from '@ktypes/models';
import { getInAppLink, matchesInAppLink } from '@kutil';
import { Subject } from 'rxjs';

interface QuestQueryParams {
  questKey: string;
  questKeyType: QuestKeyType;
  questReturnUrl: string;
}

interface DialogueQueryParams {
  refKey: string;
  logicKey: string;
  returnUrl: string;
  isQuestCard: boolean;
  cardRequestType: CardRequestType;
  dialogueId: string;
  promoId?: string;
}

type CTAQueryParams = Record<string, never> | QuestQueryParams | DialogueQueryParams;

interface NavigationOptions {
  route: string;
  queryParams: CTAQueryParams;
}

@Component({
  selector: 'kp-call-to-action',
  templateUrl: './call-to-action.component.html',
  styleUrls: ['./call-to-action.component.scss'],
})
export class CallToActionComponent implements OnChanges, OnDestroy {
  @Input() callToAction: string;
  @Input() card: CardItem;
  @Input() isQuestCard = false;
  @Input() link: Link;
  @Input() promo: Promo;
  @Input() requestType: CardRequestType;
  @Input() returnUrl: string;
  @Input() showCompactView = false;

  @Output() closeCard = new EventEmitter<boolean>();
  @Output() callToActionClicked = new EventEmitter();

  private _destroy$ = new Subject<void>();

  cardLinkType = LinkType;
  deepLinkBase = DeepLinkBase;
  event: CardEvent;
  navigationOptions: NavigationOptions;

  constructor(
    private _cardBloc: CardBloc,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _router: Router
  ) {}

  ngOnChanges() {
    if (this.link?.type === LinkType.INTERNAL) {
      if (this.requestType) {
        this.event = new CardEvent(this.card.id, this.card, CardEventType.TRACK, this.requestType);
      }
      this._setRouterLinkOptions();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  handleCardCTAClick(event) {
    this.callToActionClicked.emit();
    const isInAppLink = matchesInAppLink(this.link?.uri, this._environmentVariablesService.config);
    if (event?.currentTarget?.tagName !== 'A' || isInAppLink) {
      event.preventDefault();
      event.stopPropagation();

      // NOTE: This is to ensure links marked EXTERNAL that are actually to pages in app use
      // Angular routing rather than refreshing the tab or opening in a new tab
      if (isInAppLink) {
        if (!this.showCompactView && this.requestType) {
          this.closeCard.emit(true);
        }
        return this._router.navigateByUrl(getInAppLink(this.link.uri));
      }
    }
    if (this.requestType) {
      if ([DeepLinkBase.QuestionSet, DeepLinkBase.Dialogue].includes(this.link?.deepLinkBase)) {
        this._cardBloc.addCompletionCard(this.card);
      }
      void this._cardBloc.handleCardEvents(this.event);
      // this ensures we close Detail View, won't get here if it closed it earlier
      this.closeCard.emit(true);
    }
  }

  private _setRouterLinkOptions() {
    switch (this.link?.deepLinkBase) {
      case DeepLinkBase.Card:
        this.navigationOptions = {
          route: `./${this.link?.deepLinkData?.contentId}`,
          queryParams: {},
        };
        break;
      case DeepLinkBase.Discover:
      case DeepLinkBase.Explore:
        this.navigationOptions = {
          route: '/discover',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Feedback:
        this.navigationOptions = {
          route: '/feedback',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Help:
        this.navigationOptions = {
          route: '/help',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Me:
        this.navigationOptions = {
          route: '/me',
          queryParams: {},
        };
        break;
      case DeepLinkBase.MyActions:
        this.navigationOptions = {
          route: '/cards/actions',
          queryParams: {},
        };
        break;
      case DeepLinkBase.MyQuests:
        this.navigationOptions = {
          route: '/cards/quests',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Quest:
        this.navigationOptions = {
          route: '/quest',
          queryParams: {
            questKey: this.link?.deepLinkData?.refKey,
            questKeyType: QuestKeyType.refKey,
            questReturnUrl: encodeURIComponent(this._router.url),
          },
        };
        break;
      case DeepLinkBase.Notifications:
        this.navigationOptions = {
          route: '/reminders',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Progress:
        this.navigationOptions = {
          route: '/progress',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Report:
        this.navigationOptions = {
          route: '/report',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Saved:
        this.navigationOptions = {
          route: '/cards/saved',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Settings:
        this.navigationOptions = {
          route: '/settings',
          queryParams: {},
        };
        break;
      case DeepLinkBase.Today:
        this.navigationOptions = {
          route: '/today',
          queryParams: {},
        };
        break;
      case DeepLinkBase.PulseSurvey:
        break;
      case DeepLinkBase.QuestionSet:
      case DeepLinkBase.Dialogue:
        this.navigationOptions = {
          route: '/dialogue',
          queryParams: {
            refKey: this.link?.deepLinkData?.refKey,
            logicKey:
              ((this.link.deepLinkData as DialogueDeepLinkData)?.logicKey as DialogueLogicKey) ||
              DialogueLogicKey.templated_question_set_dialogue,
            returnUrl: getTypedDeepLinkData<DialogueDeepLinkData>(this.link?.deepLinkData)?.reportId
              ? `/report/${getTypedDeepLinkData<DialogueDeepLinkData>(this.link?.deepLinkData)?.reportId}/new`
              : this.returnUrl || this._router.url,
            isQuestCard: this.isQuestCard || null,
            cardRequestType: this.requestType,
            dialogueId: getTypedDeepLinkData<DialogueDeepLinkData>(this.link?.deepLinkData)?.userDialogueId,
            promoId: this.promo?.id,
          },
        };
        break;
      default:
        this.navigationOptions = { route: '', queryParams: {} };
    }
  }
}
